import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, RefreshCw, ChevronDown, Mail, Building 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ContactStatusBadge, formatDate } from './StatusBadges';

export const ContactsList = ({ 
  contacts, 
  loading, 
  newCount,
  onContactClick,
  onStatusUpdate 
}) => {
  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle className="flex items-center justify-between">
          <span>Contact Messages ({contacts.length})</span>
          {newCount > 0 && (
            <Badge className="bg-amber-100 text-amber-800 animate-pulse">
              {newCount} New
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-gold animate-spin" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-steel-300 mx-auto mb-4" />
            <p className="text-steel-500">No contact messages found</p>
          </div>
        ) : (
          <div className="divide-y divide-steel-100">
            <AnimatePresence>
              {contacts.map((contact, index) => {
                const isNew = contact.status === 'new';
                
                return (
                  <motion.div
                    key={contact.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.05 }}
                    className={`p-4 hover:bg-steel-50 cursor-pointer transition-colors ${isNew ? 'bg-gold/5' : ''}`}
                    onClick={() => {
                      onContactClick(contact);
                      // Auto-mark as read when opened
                      if (contact.status === 'new') {
                        onStatusUpdate(contact.id, 'read');
                      }
                    }}
                    data-testid={`contact-row-${contact.id}`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isNew ? 'bg-amber-100 text-amber-600' : 'bg-steel-100 text-steel-600'
                      }`}>
                        <Mail className="w-5 h-5" />
                      </div>

                      {/* Main Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-navy-800">{contact.name}</span>
                          {isNew && (
                            <Badge className="bg-gold text-navy-800 text-xs">New</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-steel-500">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {contact.email}
                          </span>
                          {contact.company && (
                            <span className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {contact.company}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="hidden md:block text-sm text-navy-800 max-w-[200px] truncate">
                        {contact.subject}
                      </div>

                      {/* Service Interest */}
                      {contact.service_interest && (
                        <div className="hidden lg:block">
                          <Badge variant="outline">{contact.service_interest}</Badge>
                        </div>
                      )}

                      {/* Date */}
                      <div className="hidden md:block text-sm text-steel-500">
                        {formatDate(contact.created_at)}
                      </div>

                      {/* Status */}
                      <div>
                        <ContactStatusBadge status={contact.status} />
                      </div>

                      {/* Arrow */}
                      <ChevronDown className="w-5 h-5 text-steel-400" />
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
